// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faVolumeHigh } from "@fortawesome/free-solid-svg-icons";
import { useGrammarPoints } from "../../i18n/localizedGrammar";
import { speakJapanese, isSpeechSupported, SLOW_SPEECH_RATE } from "../../utils/speak";
import SlowTurtleIcon from "../SpeakButton/SlowTurtleIcon";
import { useGrammarProgress } from "../../hooks/useGrammarProgress";
import { useAuth } from "../../context/AuthContext";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import Seo from "../Seo/SeoComponent";
import { LearnedBadge, LearningStatusButton } from "../LearningStatus/LearningStatus";
import TableCell from "../ui/TableCell/TableCell";
import BackLink from "../ui/BackLink/BackLink";
import styles from "./grammarDetailPage.module.scss";

const speechSupported = isSpeechSupported();

function SpeakButton({ text, slow = false }) {
    const { t } = useTranslation("grammarDetailPage");
    if (!speechSupported || !text) return null;
    return (
        <button
            type="button"
            className={slow ? `${styles.speakButton} ${styles.speakButtonSlow}` : styles.speakButton}
            aria-label={slow ? t("playSlowPronunciation", { text }) : t("playPronunciation", { text })}
            onClick={() => speakJapanese(text, slow ? { rate: SLOW_SPEECH_RATE } : undefined)}
        >
            {slow ? <SlowTurtleIcon /> : <FontAwesomeIcon icon={faVolumeHigh} />}
        </button>
    );
}

function GrammarDetailPage() {
    const { t, i18n } = useTranslation("grammarDetailPage");
    const { id } = useParams();
    const grammarPoints = useGrammarPoints();
    const pointIndex = grammarPoints.findIndex((entry) => entry.id === id);
    const point = pointIndex === -1 ? undefined : grammarPoints[pointIndex];
    const previousPoint = pointIndex > 0 ? grammarPoints[pointIndex - 1] : null;
    const nextPoint = pointIndex !== -1 && pointIndex < grammarPoints.length - 1 ? grammarPoints[pointIndex + 1] : null;
    const { status: authStatus } = useAuth();
    const { progress, setLearned } = useGrammarProgress();
    const learned = authStatus === "authenticated" && point ? progress[point.id] === "learned" : false;

    const isPt = i18n.language === "pt";
    const listPath = isPt ? "/pt/grammar" : "/grammar";
    const pointPath = (pointId) => (isPt ? `/pt/grammar/${pointId}` : `/grammar/${pointId}`);
    const currentPath = isPt ? `/pt/grammar/${id}` : `/grammar/${id}`;

    // Previous/Next (below) moves between separate routes rather than
    // scrolling within one page the way the Lessons page's equivalent does,
    // so without this, landing on the next grammar point would keep
    // whatever scroll offset the previous one was left at.
    useEffect(() => {
        if (!point) return;
        const prefersReducedMotion =
            typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        window.scrollTo({ top: 0, behavior: prefersReducedMotion ? "auto" : "smooth" });
    }, [id, point]);

    if (!point) {
        return (
            <div className={styles.page}>
                <Seo title={t("notFoundTitle")} path={currentPath} noindex lang={isPt ? "pt-PT" : undefined} />
                <Header />
                <main className={styles.main}>
                    <section className={styles.container}>
                        <h1 className={styles.title}>{t("notFoundTitle")}</h1>
                        <p className={styles.description}>{t("notFoundDescription", { id })}</p>
                        <BackLink to={listPath} label={t("backToList")} />
                    </section>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <Seo
                title={`${point.pattern} - ${point.title}`}
                description={point.meaning}
                path={currentPath}
                lang={isPt ? "pt-PT" : undefined}
            />
            <Header />
            <main className={styles.main}>
                <article className={styles.container}>
                    <BackLink to={listPath} label={t("backToList")} />

                    <header className={styles.grammarHeader}>
                        <div className={styles.grammarIdentity}>
                            {point.category === "writing-convention" ? (
                                <span className={styles.jlptBadge}>{t("writingConvention")}</span>
                            ) : (
                                <span className={styles.jlptBadge}>N{point.jlptLevel}</span>
                            )}
                            <h1 className={styles.title}>{point.title}</h1>
                            <p className={styles.pattern}>{point.pattern}</p>
                        </div>
                        <div className={styles.learningActions}>
                            {learned && <LearnedBadge />}
                            <LearningStatusButton
                                learned={learned}
                                onClick={() => setLearned(point.id, !learned)}
                            />
                        </div>
                    </header>

                    <section className={styles.card}>
                        <h2 className={styles.sectionHeading}>{t("meaning")}</h2>
                        <p className={styles.cardText}>{point.meaning}</p>
                    </section>

                    <section className={styles.card}>
                        <h2 className={styles.sectionHeading}>{t("explanation")}</h2>
                        <p className={styles.cardText}>{point.explanation}</p>
                    </section>

                    <section className={styles.card}>
                        <h2 className={styles.sectionHeading}>{t("formation")}</h2>
                        <p className={styles.formationText}>{point.formation}</p>
                    </section>

                    {point.tables?.map((table) => (
                        <section key={table.caption} className={styles.card}>
                            <div className={styles.tableScroll} tabIndex={0} role="region" aria-label={table.caption}>
                                <table className={styles.table}>
                                    <caption className={styles.tableCaption}>{table.caption}</caption>
                                    <thead>
                                        <tr>
                                            {table.headers.map((header) => (
                                                <th key={header} scope="col">
                                                    {header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {table.rows.map((row, rowIndex) => (
                                            <tr key={rowIndex}>
                                                {row.map((cell, cellIndex) =>
                                                    cellIndex === 0 ? (
                                                        <th key={cellIndex} scope="row">
                                                            <TableCell cell={cell} />
                                                        </th>
                                                    ) : (
                                                        <td key={cellIndex}>
                                                            <TableCell cell={cell} />
                                                        </td>
                                                    )
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    ))}

                    <section>
                        <h2 className={styles.sectionHeading}>{t("examples")}</h2>
                        <ul className={styles.exampleList}>
                            {point.examples.map((example) => (
                                <li key={example.japanese} className={styles.exampleItem}>
                                    <div className={styles.exampleJapaneseRow}>
                                        <span className={styles.exampleJapanese}>{example.japanese}</span>
                                        <SpeakButton text={example.japanese} />
                                        <SpeakButton text={example.japanese} slow />
                                    </div>
                                    <p className={styles.exampleReading}>{example.reading}</p>
                                    <p className={styles.exampleEnglish}>{example.translation}</p>
                                </li>
                            ))}
                        </ul>
                    </section>

                    {point.related?.length > 0 && (
                        <section className={styles.card}>
                            <h2 className={styles.sectionHeading}>{t("related")}</h2>
                            <ul className={styles.relatedList}>
                                {point.related.map((link) => (
                                    <li key={link.to}>
                                        <Link to={link.to} className={styles.relatedLink}>
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    <nav className={styles.grammarNavRow} aria-label={t("grammarNavigation")}>
                        {previousPoint ? (
                            <Link to={pointPath(previousPoint.id)} className={styles.grammarNavButton}>
                                <FontAwesomeIcon icon={faChevronLeft} className={styles.grammarNavIcon} />
                                <span className={styles.grammarNavText}>
                                    <span className={styles.grammarNavLabel}>{t("previous")}</span>
                                    <span className={styles.grammarNavTitle}>{previousPoint.title}</span>
                                </span>
                            </Link>
                        ) : (
                            <button type="button" disabled className={styles.grammarNavButton}>
                                <FontAwesomeIcon icon={faChevronLeft} className={styles.grammarNavIcon} />
                                <span className={styles.grammarNavText}>
                                    <span className={styles.grammarNavLabel}>{t("previous")}</span>
                                </span>
                            </button>
                        )}
                        {nextPoint ? (
                            <Link
                                to={pointPath(nextPoint.id)}
                                className={`${styles.grammarNavButton} ${styles.grammarNavButtonNext}`}
                            >
                                <span className={styles.grammarNavText}>
                                    <span className={styles.grammarNavLabel}>{t("next")}</span>
                                    <span className={styles.grammarNavTitle}>{nextPoint.title}</span>
                                </span>
                                <FontAwesomeIcon icon={faChevronRight} className={styles.grammarNavIcon} />
                            </Link>
                        ) : (
                            <button
                                type="button"
                                disabled
                                className={`${styles.grammarNavButton} ${styles.grammarNavButtonNext}`}
                            >
                                <span className={styles.grammarNavText}>
                                    <span className={styles.grammarNavLabel}>{t("next")}</span>
                                </span>
                                <FontAwesomeIcon icon={faChevronRight} className={styles.grammarNavIcon} />
                            </button>
                        )}
                    </nav>

                    <p className={styles.attribution}>{t("attribution")}</p>
                </article>
            </main>
            <Footer />
        </div>
    );
}

export default GrammarDetailPage;
